import React, { useState, useEffect  } from 'react';
import { Button, ButtonGroup, Dialog, DialogActions,DialogContentText, DialogContent, DialogTitle, Divider, FormControl, FormHelperText, TextField, InputLabel, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SendIcon from '@mui/icons-material/Send';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { newNode, newEdge, genNode } from '../utils/ideaTool';
import LexicalEditor from "./Editor";


export const NodeCreator = ({ open, onClose, ws , nodePlan, onNodeCreated}) => {

    const name = localStorage.getItem('name');
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState();
    const [title, setTitle] = useState("");

    // 從 HTML 內容中提取純文字（用於標題，限制長度）
    const extractTextFromHTML = (html, maxLength = 50) => {
        if (!html) return '';
        
        // 創建臨時 DOM 元素來解析 HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // 獲取純文字內容
        let text = tempDiv.textContent || tempDiv.innerText || '';
        
        // 移除多餘的空白和換行
        text = text.replace(/\s+/g, ' ').trim();
        
        // 如果指定了最大長度，則限制長度（用於標題）
        if (maxLength && text.length > maxLength) {
            text = text.substring(0, maxLength) + '...';
        }
        
        return text;
    };
    
    // 從 HTML 內容中提取純文字（用於內容，不限制長度）
    const extractTextFromHTMLForContent = (html) => {
        if (!html) return '';
        
        // 創建臨時 DOM 元素來解析 HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // 獲取純文字內容
        let text = tempDiv.textContent || tempDiv.innerText || '';
        
        // 移除多餘的空白和換行，但保留換行符號（轉換為空格）
        text = text.replace(/\s+/g, ' ').trim();
        
        return text;
    };

    const titleContentChange = (e) => {
        const value = e.target.value;
        setTitle(value);
    };


    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // 檢查是否為問題牆回復（不需要標題）
      const isQuestionWallReply = nodePlan.wall_type === 'question' && (nodePlan.tags === 'reply' || nodePlan.tags === 'question');
      
      // 如果不是問題牆回復，需要驗證標題
      if (!isQuestionWallReply) {
        const isTitleValid = title.trim().length > 0;
        const titleValidLength = title.trim().length <= 15; // 允許剛好15個字
        if(
          !isTitleValid || 
          !titleValidLength || 
          !content || 
          !content.length > 0
        ) {
          return alert("請確定以下項目： \n1. 標題及內容都已輸入\n2. 標題長度不超過15個字");
        }
      } else {
        // 問題牆回復只需要內容
        if (!content || !content.length > 0) {
          return alert("請輸入內容");
        }
      }
      // 獲取 course_dispatch_id 和 group_id
      const courseDispatchId = sessionStorage.getItem('courseDispatchId') || sessionStorage.getItem('activityId');
      
      // 檢查用戶是否是老師（老師可以為任意小組創建節點）
      const isTeacher = sessionStorage.getItem('isTeacher') === 'true' || 
                       sessionStorage.getItem('userRole') === 'teacher' ||
                       localStorage.getItem('role') === 'teacher';
      
      let groupId;
      if (isTeacher) {
        // 老師可以使用 viewingGroupId（正在查看的小組）、URL 參數的 groupId、或 sessionStorage 的 groupId
        // 這樣老師就可以在跨組查看時為該小組創建節點
        // 優先順序：URL 參數 > viewingGroupId > sessionStorage groupId
        const urlParams = new URLSearchParams(window.location.search);
        const urlGroupId = urlParams.get('groupId');
        const pathGroupId = window.location.pathname.match(/\/idea-wall\/\d+\/(\d+)/)?.[1]; // 從路徑中提取 groupId
        const viewingGroupId = sessionStorage.getItem('viewingGroupId');
        const sessionGroupId = sessionStorage.getItem('groupId');
        
        groupId = pathGroupId || urlGroupId || viewingGroupId || sessionGroupId;
        
        console.log('👨‍🏫 [NodeCreator] 老師模式，使用的小組ID:', {
          'pathGroupId (URL路徑)': pathGroupId,
          'urlGroupId (URL參數)': urlGroupId,
          'viewingGroupId (sessionStorage)': viewingGroupId,
          'sessionGroupId (sessionStorage)': sessionGroupId,
          '最終使用': groupId
        });
      } else {
        // 學生只能使用 myGroupId（實際所在的小組），不能使用 viewingGroupId
        // 這樣學生在跨組查看時就不能為其他小組創建節點
        const myGroupId = sessionStorage.getItem('myGroupId');
        groupId = myGroupId || sessionStorage.getItem('groupId');
        console.log('👨‍🎓 [NodeCreator] 學生模式，使用的小組ID:', {
          'myGroupId (優先)': myGroupId,
          'groupId (備用)': sessionStorage.getItem('groupId'),
          '最終使用': groupId
        });
      }
      
      // 驗證必要欄位
      if (!courseDispatchId) {
        alert("錯誤：找不到課程派發ID，請重新進入頁面");
        setLoading(false);
        return;
      }
      
      if (!groupId) {
        // 如果是老師，提供更詳細的錯誤信息
        if (isTeacher) {
          console.error('❌ [NodeCreator] 老師模式但找不到小組ID:', {
            'pathname': window.location.pathname,
            'viewingGroupId': sessionStorage.getItem('viewingGroupId'),
            'groupId': sessionStorage.getItem('groupId'),
            'URL參數': new URLSearchParams(window.location.search).get('groupId')
          });
          alert("錯誤：找不到小組ID。請確保您正在查看特定小組的想法牆，或從小組列表選擇一個小組。");
        } else {
          alert("錯誤：找不到小組ID，請重新進入頁面");
        }
        setLoading(false);
        return;
      }
      
      // 如果標題為空或太短，嘗試從內容中提取
      // 問題牆回復不需要標題，直接使用內容的前50字作為標題
      let finalTitle = title.trim();
      if (isQuestionWallReply) {
        // 問題牆回復：使用內容的前50字作為標題
        // 確保從 HTML 中提取純文字，並移除可能的初始引導文字
        let extractedText = extractTextFromHTML(content);
        
        // 移除可能的初始引導文字（如果用戶沒有修改內容）
        const initialPlaceholders = [
          '早安！',
          '請在這邊打一些你的想法吧',
          '請輸入你的問題...',
          '請輸入你的回覆...',
          '請輸入你的想法...',
          '請輸入內容...'
        ];
        
        // 如果提取的文字只包含初始引導文字，則使用空標題
        const isOnlyPlaceholder = initialPlaceholders.some(placeholder => 
          extractedText.trim() === placeholder || extractedText.trim().startsWith(placeholder)
        );
        
        if (isOnlyPlaceholder) {
          extractedText = '';
        }
        
        finalTitle = extractedText.substring(0, 50);
        console.log('問題牆回復：從內容中提取的標題:', finalTitle);
        console.log('  - 原始內容:', content);
        console.log('  - 提取的文字:', extractedText);
      } else if (finalTitle.length === 0 && content) {
        finalTitle = extractTextFromHTML(content);
        console.log('從內容中提取的標題:', finalTitle);
      }
      
      // 根據節點類型設置顏色
      const getNodeColor = (nodeType) => {
        const colorMap = {
          'reply': '#FFFFFF',        // 回覆 - 白色
          'idea': '#FFF9C4',         // 想法 - 黃色
          'question': '#BBDEFB',     // 提問 - 藍色
          'info': '#C8E6C9',  // 補充資訊 - 綠色
          'experiment': '#FFCDD2',   // 實驗 - 紅色
          'record': '#B2EBF2'        // 記錄 - 青色
        };
        return colorMap[nodeType] || '#FFFFCC';
      };
      
      const nodeType = nodePlan.tags || "idea";
      // 從 nodePlan 中讀取 wall_type，如果沒有則默認為 'idea'
      const wallType = nodePlan.wall_type || 'idea';
      // 從 nodePlan 中讀取 task_id（用於問題牆關聯關鍵提問）
      const taskId = nodePlan.task_id || null;
      // 從 nodePlan 中讀取 parent_node_id（用於回復節點）
      const parentNodeId = nodePlan.parentNodeId || null;
      // 從 nodePlan 中讀取 course_dispatch_id 和 group_id（如果提供）
      const finalCourseDispatchId = nodePlan.course_dispatch_id ? parseInt(nodePlan.course_dispatch_id) : parseInt(courseDispatchId);
      const finalGroupId = nodePlan.group_id ? parseInt(nodePlan.group_id) : parseInt(groupId);
      
      // 將 HTML 內容轉換為純文字（保留鷹架文字，但刪除 HTML 標籤）
      // 對於問題牆，我們希望保存純文字而不是 HTML
      console.log('🔍 [NodeCreator] 開始處理內容轉換:');
      console.log('  - content 類型:', typeof content);
      console.log('  - content 值:', content);
      console.log('  - content 是否為字符串:', typeof content === 'string');
      console.log('  - content 是否為空:', !content);
      
      let finalContent = content;
      if (content && typeof content === 'string') {
        // 檢查是否包含 HTML 標籤
        const hasHTMLTags = /<[^>]+>/.test(content);
        console.log('  - 是否包含 HTML 標籤:', hasHTMLTags);
        console.log('  - HTML 標籤檢查結果:', /<[^>]+>/.test(content));
        
        if (hasHTMLTags) {
          // 提取純文字，保留所有文字內容（包括鷹架文字），不限制長度
          finalContent = extractTextFromHTMLForContent(content);
          console.log('📝 [NodeCreator] 將 HTML 內容轉換為純文字:');
          console.log('  - 原始內容 (HTML):', content);
          console.log('  - 轉換後內容 (純文字):', finalContent);
        } else {
          console.log('  - 內容不包含 HTML 標籤，跳過轉換');
        }
      } else {
        console.log('  - ⚠️ content 為空或不是字符串，跳過轉換');
      }
      
      console.log('  - 最終使用的內容:', finalContent);
      
      const ideaData = {
        course_dispatch_id: finalCourseDispatchId,
        group_id: finalGroupId,
        wall_type: wallType, // 從 nodePlan 讀取牆面類型
        node_type: nodeType,
        title: finalTitle, // 使用處理後的標題
        content: finalContent, // 使用處理後的內容（純文字）
        position_x: Math.random() * 400 + 100,
        position_y: Math.random() * 300 + 100,
        size_width: 250,
        size_height: 180,
        color: getNodeColor(nodeType), // 根據類型設置顏色
        ...(taskId && { task_id: parseInt(taskId) }), // 如果有 task_id，添加到數據中
        ...(parentNodeId && { parent_node_id: parseInt(parentNodeId) }) // 如果有 parent_node_id，添加到數據中
      };
      
      console.log('📤 [NodeCreator] 準備創建節點，完整數據:', ideaData);
      console.log('  - task_id:', ideaData.task_id || '無');
      console.log('  - parent_node_id:', ideaData.parent_node_id || '無');
      console.log('  - content (最終):', ideaData.content);
      console.log('  - content 類型:', typeof ideaData.content);
      console.log('  - content 是否包含 HTML:', /<[^>]+>/.test(ideaData.content || ''));
      
      console.log('準備創建節點，數據:', ideaData);
      setLoading(true);
      try {
        // 使用 courseDispatchId 作為 activityId
        const activityId = courseDispatchId;
        const responseFromPostNode = await newNode(ideaData, activityId, ws);
        
        console.log('✅ newNode 返回成功');
        console.log('  - 返回的數據:', responseFromPostNode.data);
        
        // 模擬 Forum.js 的節點處理邏輯
        const createdNode = responseFromPostNode.data.data;
        console.log('  - createdNode:', createdNode);
        console.log('  - createdNode 的所有鍵:', Object.keys(createdNode || {}));
        console.log('  - createdNode.node_type:', createdNode.node_type);
        console.log('  - createdNode.author_name:', createdNode.author_name);
        console.log('  - createdNode.Author:', createdNode.Author);
        console.log('  - createdNode.createdAt:', createdNode.createdAt);
        console.log('  - createdNode.created_at:', createdNode.created_at);
        
        // 轉換 node_type 為 tags，以匹配 genNode 的期望格式
        const nodeForGenNode = {
          ...createdNode,
          tags: createdNode.node_type, // 轉換 node_type 為 tags
          author: createdNode.author_name || createdNode.Author?.name || '未知',
          createdAt: createdNode.createdAt || createdNode.created_at || new Date().toISOString()
        };
        
        console.log('  - 轉換後的節點數據:', nodeForGenNode);
        console.log('  - 轉換後的 nodeForGenNode.tags:', nodeForGenNode.tags);
        console.log('  - 轉換後的 nodeForGenNode.author:', nodeForGenNode.author);
        console.log('  - 轉換後的 nodeForGenNode.createdAt:', nodeForGenNode.createdAt);
        
        const processedNode = {
          createdAt: nodeForGenNode.createdAt,
          ...genNode(nodeForGenNode),
        };
        
        console.log('處理後的節點:', processedNode);
        console.log('✅ 節點創建成功，ID:', createdNode.id);
        console.log('🔍 檢查是否需要創建邊緣 - nodePlan.newEdge:', nodePlan?.newEdge);
        
        if(nodePlan?.newEdge){
          console.log('✅ 需要創建邊緣');
          
          // 優先使用 nodePlan 中的 parentNodeId，否則從 sessionStorage 獲取
          const nodeIdFromStorage = sessionStorage.getItem('nodeId');
          const parentNodeId = nodePlan.parentNodeId || nodeIdFromStorage;
          const fromNodeId = parentNodeId ? parseInt(parentNodeId) : null;
          const toNodeId = responseFromPostNode.data.data.id;
          
          console.log('🔗 準備創建回覆邊緣');
          console.log('  - nodePlan.parentNodeId:', nodePlan.parentNodeId);
          console.log('  - nodeIdFromStorage:', nodeIdFromStorage);
          console.log('  - fromNodeId:', fromNodeId);
          console.log('  - toNodeId:', toNodeId);
          
          if (!fromNodeId || isNaN(fromNodeId)) {
            console.warn('⚠️ 無法創建邊緣：原節點 ID 無效', fromNodeId);
          } else {
            const edgeData = {
              course_dispatch_id: parseInt(courseDispatchId),
              group_id: parseInt(groupId),
              from_node_id: fromNodeId,
              to_node_id: toNodeId,
              edge_type: 'reply'
            };
            console.log('📤 發送邊緣創建請求:', edgeData);
            console.log('➡️ 箭頭方向: 原節點 -> 回覆節點（想法延伸）');
            
            try {
              const responseFromPostEdge = await newEdge(edgeData, activityId, ws);
              console.log('✅ 邊緣創建成功，響應:', responseFromPostEdge.data);
            } catch (edgeError) {
              console.error('❌ 邊緣創建失敗:', edgeError);
            }
          }
        }
        
        alert("新增成功");
        console.log('🎉 所有操作完成，關閉對話框');
        
        // 通知父組件節點已創建，立即更新本地狀態
        if (onNodeCreated) {
          console.log('📢 [NodeCreator] 通知父組件節點已創建');
          onNodeCreated(createdNode);
        }
        
        onClose();
        setLoading(false);
      }
      catch(error){
        alert("新增失敗");
          if (error.response) {
              // console.log(error.response);
              // console.log("server responded");
              setLoading(false);
          } else if (error.request) {
              // console.log("network error");
              setLoading(false);
          } else {
              // console.log(error);
              setLoading(false);
          }
      }; 
    };
    useEffect(() => {
      if (open) {
        setTitle("");
      }
    }, [open]);
    
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          scroll='body'
        >
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <span>{nodePlan.title}</span>
              {nodePlan.nodeTypeOptions && (
                <ToggleButtonGroup
                  value={nodePlan.selectedNodeType}
                  exclusive
                  onChange={(e, newType) => {
                    if (newType !== null && nodePlan.onNodeTypeChange) {
                      nodePlan.onNodeTypeChange(newType);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontSize: '12px',
                      padding: '4px 12px',
                      textTransform: 'none',
                      border: '1px solid #e0e0e0',
                      '&.Mui-selected': {
                        backgroundColor: '#1e4d5f',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#0d3b4a'
                        }
                      }
                    }
                  }}
                >
                  {nodePlan.nodeTypeOptions.map(option => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </div>
          </DialogTitle>

          <Divider variant="middle" />
          <DialogContent>
            {nodePlan.note && (
              <DialogContentText id="alert-dialog-description" >   
              <>
                  正在回應：
                  <div dangerouslySetInnerHTML={{ __html: nodePlan.note }} style={{padding: '10px'}} />
              </>
              </DialogContentText>
            )}
            {/* 問題牆回復不需要標題輸入框 */}
            {!(nodePlan.wall_type === 'question' && (nodePlan.tags === 'reply' || nodePlan.tags === 'question')) && (
              <>
                回應標題：
                <FormControl variant="standard" fullWidth>
                  <TextField
                    required
                    id="standard-required"
                    autoFocus
                    margin="dense"
                    label={nodePlan.lable}
                    type="text"
                    name='title'
                    value={title}
                    fullWidth
                    variant="standard"
                    onChange={titleContentChange}
                    inputProps={{ maxLength: 15 }}
                    helperText={`${title.length}/15 字 - ${nodePlan.helperMsg}`}
                  />
                </FormControl>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                '& > *': {
                  m: 1,
                },
              }}
            >

              <LexicalEditor 
                changeFunc={(content)=>{
                  console.log("content: ",content);
                  setContent(content);
                }}
                nodePlan={nodePlan}
                
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={onClose}>取消</Button>
            <LoadingButton 
              type='submit' 
              onClick={handleSubmit} 
              loading={loading} 
              loadingPosition="start" 
              startIcon={<SendIcon />}
              variant="contained"
            >
              送出
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
}